import { BaseCalendarEventFragment, BaseUserFragment, CalendarEventLocation } from '../../../generated/graphql.generated';
import { dayjs } from '../../core/dayjs/dayjs';

import { EventAttendeeRoles, eventTypeInfoMap, JOB_ROLE_MAP } from '../../global.constants';

export type JobRole = (typeof JOB_ROLE_MAP)[keyof typeof JOB_ROLE_MAP];

export interface UserWithName extends BaseUserFragment {
    name: string;
    role?: JobRole;
}

export interface JobsV2EventLocation {
    id: string;
    locationId: string;
    type: string;
    address: string;
    addressLabel: string;
    order: number;
    estimatedTimeAtLocation: number;
    totalTravelTimeToNextLocation: number;
    ignoreInDuration: boolean;
}

export interface JobEvent {
    title: string;
    details: string;
    datetime: string;
    style: { borderLeftColor: string };
    locations: JobsV2EventLocation[];
    crew: UserWithName[];
    event: BaseCalendarEventFragment;
}

function formatDateTimeWithTimezone(timestamp: number, timezone: string) {
    const dayJsObject = dayjs(timestamp * 1000).tz(timezone);
    if (!dayJsObject.isValid()) {
        return { date: 'Invalid Date', time: 'Invalid Time' };
    }

    return {
        date: dayJsObject.format('MMM DD YYYY'),
        time: dayJsObject.get('minute') === 0 ? dayJsObject.format('hA') : dayJsObject.format('h:mmA'),
    };
}

export const formatEventLocations = (location: CalendarEventLocation): JobsV2EventLocation => ({
    address: location.location?.addressLineOne || '',
    addressLabel: `${location.type}: ${location.location?.addressLineOne || ''}`,
    id: location.id,
    locationId: location.location?.id || '',
    type: location.type ?? '',
    estimatedTimeAtLocation: location.estimatedTimeAtLocation ?? 0,
    totalTravelTimeToNextLocation: (location.travelTimeToNextLocation || 0) + (location.travelTimeToNextLocationOffset || 0),
    ignoreInDuration: location.ignoreInDuration ?? false,
    order: location.order,
});

const getAssetDetails = (assets: BaseCalendarEventFragment['assets'] = []) => {
    const assetList = assets ?? [];
    if (!assetList.length) return '';

    const assetTypes = Array.from(new Set(assetList.map(asset => asset?.type)));
    const assetDetails = assetTypes.map(assetType => {
      const filteredAssets = assetList.filter(asset => asset?.type === assetType);
      const assetNames = filteredAssets.map(asset => asset?.name || assetType).join(', ');
      return `${filteredAssets.length} ${filteredAssets.length > 1 ? `${assetType}s` : assetType} (${assetNames})`;
    });

    return assetDetails.join(' | ');
};

export const formatEventDetails = (event: BaseCalendarEventFragment, timezone: string): JobEvent => {
    const { title: eventTitle, status, start, end } = event;
    const assets = event.assets ?? [];
    const attendees = event.attendees ?? [];
    const locations = event.locations ?? [];

    const title = [eventTitle, status].filter(Boolean).join(' - ');

    let eventDate = 'Not Booked';
    let eventTime = null;
    let duration = null;

    if (start && end) {
      const startDateFormatted = formatDateTimeWithTimezone(start, timezone);
      const endDateFormatted = formatDateTimeWithTimezone(end, timezone);

      eventDate = `${startDateFormatted.date} - ${endDateFormatted.date}`;
      eventTime = `${startDateFormatted.time} - ${endDateFormatted.time}`;

      const dayJsStart = dayjs(start * 1000).tz(timezone);
      const dayJsEnd = dayjs(end * 1000).tz(timezone);
      const durationMinutes = dayJsEnd.diff(dayJsStart, 'minute');
      duration = Number((durationMinutes / 60).toFixed(2));
    }

    const assetDetails = getAssetDetails(assets);


    const crew = attendees.filter(({ role }) => role !== EventAttendeeRoles.author);
    const crewCount = crew.length;
    const totalCrew = crewCount ? `${crewCount} Crew` : '';

    const datetime = [
      eventDate,
      eventTime,
      duration && `${duration} hrs`,
    ].filter(Boolean).join(' | ');

    const details = [
      eventDate,
      eventTime,
      duration && `${duration} hrs`,
      //timezone && duration ? `(${timezone})` : '',
      assetDetails,
      totalCrew
    ].filter(Boolean).join(' | ');

    const style = {
      borderLeftColor: event.colour ?? eventTypeInfoMap[event.type]?.backgroundColor,
    };

    // Event details for editing an event
    const derivedCrew = crew.map((attendee) => ({
      ...attendee.user,
      role: attendee.role as JobRole,
      name: [attendee.user.givenName, attendee.user.familyName].filter(Boolean).join(' '),
    }));

    const derivedLocations = locations
      ? locations.map(formatEventLocations).sort((a, b) => a.order - b.order)
      : [];

    // Sort locations in the original event copy as well (used in bookings dialog)
    const sortedLocations = event.locations ? [...event.locations].sort((a, b) => a.order - b.order) : [];

    return {
      title,
      datetime,
      details,
      style,
      event: {
          ...event,
          locations: sortedLocations,
      },
      crew: derivedCrew,
      locations: derivedLocations,
    };
};
